import { ChangeEvent, Fragment, FunctionComponent } from 'react';
import { Checkbox } from '../Checkbox/Checkbox';
import {
    MoneyInputFieldWrapper,
    NumberInputFieldWrapper,
    TextFieldWrapper,
} from '@/pageContents/liftOperatorsContent/formBlocks/InputFields';
import { FacilitiesContainer } from '@/pageContents/liftOperatorsContent/formBlocks/facility/FacilitiesContainer';
import { LiftOperatorFormField } from '@/pageContents/liftOperatorsContent/formDataConfig';
import { Money } from '@/models/money';
import { CreateFacility, Facility } from '@/models/facility';
import { get, unionBy } from 'lodash';
import {
    CreateLiftOperator,
    isOfTypeLiftOperator,
    LiftOperator,
} from '@/models/liftoperator';
import { SeoMetaDataBlock } from '@/pageContents/liftOperatorsContent/formBlocks/SeoMetaData';
import {
    createSeoMetaDataForAllLanguages,
    SeoMetaData,
} from '@/models/seoMetaData';
import { Typography } from '@mui/material';
import { LogoUploadContainer } from '@/components/FormField/LogoUploadContainer';
import { MarkdownInputFormWrapper } from '@/components/FormField/MarkdownInputFormWrapper';

type FormFieldProps = {
    liftOperator: LiftOperator | CreateLiftOperator;
    dependsOnValue?: string;
    onCheckboxChange: (e) => void;
    onTextInputChange: (
        nextVal: number | string | Money,
        e: ChangeEvent<HTMLInputElement>
    ) => void;
    onFacilitiesChange: (
        value: (Facility | CreateFacility)[],
        name: string
    ) => void;
    onSeoMetaDataChange: (value: SeoMetaData) => void;
    field: LiftOperatorFormField;
};

export const FormField: FunctionComponent<FormFieldProps> = ({
    field,
    liftOperator,
    onCheckboxChange,
    onTextInputChange,
    onFacilitiesChange,
    onSeoMetaDataChange,
}) => {
    const { name, label, component, options, children, getValue } = field;

    const fieldValue = getValue
        ? getValue(liftOperator, field)
        : get(liftOperator, name, null);

    const basicInputProps = {
        name,
        label,
        sx: {
            width: '100%',
        },
        value: fieldValue,
    };
    return (
        <Fragment>
            {component === 'checkbox' && (
                <Checkbox {...basicInputProps} onChange={onCheckboxChange} />
            )}

            {component === 'textInput' && (
                <TextFieldWrapper
                    {...basicInputProps}
                    onBlur={onTextInputChange}
                />
            )}

            {component === 'select' && (
                <Fragment>
                    <TextFieldWrapper
                        {...basicInputProps}
                        SelectProps={{
                            native: true,
                        }}
                        fullWidth
                        select
                        onChange={onTextInputChange}
                    >
                        {options.map(({ label, value }, index) => (
                            <option key={index} value={value}>
                                {label}
                            </option>
                        ))}
                    </TextFieldWrapper>
                </Fragment>
            )}

            {component === 'numberInput' && (
                <NumberInputFieldWrapper
                    {...basicInputProps}
                    onBlur={onTextInputChange}
                />
            )}

            {component === 'moneyInput' && (
                <MoneyInputFieldWrapper
                    {...basicInputProps}
                    onBlur={onTextInputChange}
                />
            )}

            {component === 'facilityInput' && (
                <FacilitiesContainer
                    {...basicInputProps}
                    onChange={onFacilitiesChange}
                />
            )}

            {component === 'markdownInput' && (
                <MarkdownInputFormWrapper
                    {...basicInputProps}
                    onBlur={onTextInputChange}
                />
            )}

            {component === 'logoUpload' &&
                isOfTypeLiftOperator(liftOperator) && (
                    <LogoUploadContainer
                        liftOperatorId={liftOperator.id}
                        label='Logo'
                        subtitle={
                            <>
                                <Typography variant='body2'>
                                    1.) Mögliche Datei-Formate: .jpg, .png
                                </Typography>
                                <Typography variant='body2'>
                                    2.) Die Größe des Logos passt starjack
                                    automatisch an
                                </Typography>
                            </>
                        }
                        type='logo'
                        imageUrl={get(liftOperator, `${name}[0].href`, '')}
                    />
                )}

            {children
                ?.filter((item) => item.dependsOnParentValue === fieldValue)
                .map((field: LiftOperatorFormField, index) => {
                    return (
                        <FormField
                            key={`${field.name}_${index}`}
                            field={field}
                            liftOperator={liftOperator}
                            onCheckboxChange={onCheckboxChange}
                            onTextInputChange={onTextInputChange}
                            onFacilitiesChange={onFacilitiesChange}
                            onSeoMetaDataChange={onSeoMetaDataChange}
                        />
                    );
                })}

            {component === 'seoMetaDataInput' && (
                <Fragment>
                    {unionBy(
                        liftOperator.metaData,
                        createSeoMetaDataForAllLanguages(),
                        'language'
                    ).map((meta, index) => (
                        <SeoMetaDataBlock
                            key={index}
                            seoMetaData={meta}
                            onChange={onSeoMetaDataChange}
                        />
                    ))}
                </Fragment>
            )}
        </Fragment>
    );
};
